/**
 * TMS save-card modal driver.
 *
 * Wires the "Add a New Payment Method" button (any element marked
 * [data-open-save-card-modal] on the host page) to:
 *   1. Show the shared SFJAZZ full-screen loader.
 *   2. Fetch /ace-api/payment/save-card-component for a TMS view (Tessitura merchant
 *      services payload + script url + checksum).
 *   3. Inject the merchant services script and mount the Tessitura PaymentComponent
 *      into the (still-hidden) modal container.
 *   4. Hide the loader and open the modal once the component is fully rendered.
 *
 * The merchant component POSTs to /ace-api/payment/save-card-authorize on submit.
 * If 3DS is required, Tessitura redirects through /ace-api/payment/save-card-finalize
 * before bouncing back to the host page with status query params.
 *
 * The same modal/script flow is also reused by the free trial signup page —
 * see `mountTmsSaveCardModal` below, which takes the authorize/finalize endpoint
 * URLs as arguments so callers can point at their own server-side endpoints.
 */

interface TmsSaveCardView {
    url: string;
    checksum?: string;
    properties: Record<string, unknown> & { payload: string };
}

interface TmsPaymentComponent {
    mount(selector: string): void;
    /** Present on Adyen-backed builds; used to tear down secured-field iframes on remount. */
    unmount?(): void;
    remove?(): void;
}

declare global {
    interface Window {
        TessituraMerchantServices?: {
            PaymentComponent: new (props: Record<string, unknown>) => TmsPaymentComponent;
        };
    }
}

const SAVE_CARD_COMPONENT_ENDPOINT = '/ace-api/payment/save-card-component';
const SAVE_CARD_AUTHORIZE_ENDPOINT = '/ace-api/payment/save-card-authorize';
const SAVE_CARD_FINALIZE_ENDPOINT = '/ace-api/payment/save-card-finalize';

export interface MountTmsSaveCardModalOptions {
    /** URL the TMS PaymentComponent POSTs the tokenized card payload to. */
    authorizeUrl: string;
    /** URL Tessitura redirects to after a 3DS challenge completes. */
    finalizeUrl: string;
    /**
     * Selectors for the openers that should trigger the modal. Defaults to
     * `[data-open-save-card-modal]` (the membership portal). Free trial page
     * passes its own button id.
     */
    openerSelector?: string;
    /**
     * Called after the user clicks an opener, just before the network fetch.
     * Return false (or a rejected promise) to abort opening the modal — e.g.
     * the free trial page uses this to validate the address form and POST it
     * to the server before letting TMS load.
     */
    beforeOpen?: () => boolean | Promise<boolean>;
    /**
     * Re-fetch and re-mount the merchant component every time an opener is clicked,
     * instead of reusing the instance from the first open.
     *
     * Needed whenever the user can retry after a declined card: the component is left
     * in a terminal error state, and simply re-showing the modal presents that dead
     * form with no way to enter a new card short of reloading the page. The payload and
     * checksum are also tied to a single authorization attempt, so a reused component
     * can fail confusingly on the second submit.
     *
     * Off by default because it re-runs `beforeOpen` on every open. The free trial page
     * POSTs a new address from that hook, so re-running it there would create duplicate
     * address records.
     */
    remountOnOpen?: boolean;
}

/**
 * Mounts the TMS save-card modal with caller-supplied endpoint URLs.
 *
 * Both the membership portal flow and the free trial signup flow use the same
 * underlying merchant component and modal HTML; they only differ in which
 * server-side endpoints back the authorize + finalize calls. Extracting this
 * helper from `initTmsSaveCardModal` keeps the script-load + mount logic in one
 * place so future flows can reuse it by passing different URLs.
 */
export function mountTmsSaveCardModal(options: MountTmsSaveCardModalOptions): void {
    const modal = document.getElementById('tms-save-card-modal');
    const openerSelector = options.openerSelector ?? '[data-open-save-card-modal]';
    const openers = document.querySelectorAll<HTMLElement>(openerSelector);
    if (!modal || openers.length === 0) return;

    const errorEl = document.getElementById('tms-save-card-error');
    const container = document.getElementById('tms-save-card-container');
    let mounted = false;
    // Guards against a second click while the component fetch/mount is still in flight,
    // which would otherwise race two components into the same container.
    let loading = false;
    let component: TmsPaymentComponent | null = null;

    /**
     * Re-resolve the shared SFJAZZ full-screen loader on every show/hide. It may
     * be rendered after this script runs, and we'd rather no-op gracefully than
     * cache a null reference.
     */
    const getLoaderEl = (): HTMLElement | null =>
        document.querySelector<HTMLElement>('.sfjazz-loading-container.sfjazz-loading-container-center')
        ?? document.querySelector<HTMLElement>('.sfjazz-loading-container');

    const showLoader = () => {
        const el = getLoaderEl();
        if (el) el.style.display = '';
    };

    const hideLoader = () => {
        const el = getLoaderEl();
        if (el) el.style.display = 'none';
    };

    const showModal = () => {
        modal.removeAttribute('hidden');
        modal.classList.add('active');
        modal.setAttribute('aria-hidden', 'false');
    };

    const hideModal = () => {
        modal.setAttribute('hidden', 'true');
        modal.classList.remove('active');
        modal.setAttribute('aria-hidden', 'true');
    };

    const showError = (msg?: string) => {
        loading = false;
        hideLoader();
        // If the mount failed, surface the error inside the modal so the user sees
        // something rather than the modal silently never opening.
        showModal();
        if (!errorEl) return;
        errorEl.textContent = msg ?? 'Could not load the card entry form. Please try again.';
        errorEl.style.display = 'block';
    };

    const loadAndMount = () => {
        if (errorEl) errorEl.style.display = 'none';
        // Drop any previous component — on a remount this is the declined form we are
        // replacing. Ask it to tear itself down first so its secured-field iframes and
        // listeners go with it; clearing innerHTML alone would orphan them.
        try {
            if (component?.unmount) component.unmount();
            else if (component?.remove) component.remove();
        } catch (err) {
            console.warn('[tms-save-card] unmount failed; clearing container anyway:', err);
        }
        component = null;
        if (container) container.innerHTML = '';
        mounted = false;
        loading = true;
        showLoader();

        fetch(SAVE_CARD_COMPONENT_ENDPOINT, {
            method: 'GET',
            credentials: 'same-origin',
            headers: { Accept: 'application/json' }
        })
            .then(r => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.json() as Promise<TmsSaveCardView>;
            })
            .then(view => injectScriptThenMount(view))
            .catch(err => {
                loading = false;
                console.error('[tms-save-card] fetch failed:', err);
                showError();
            });
    };

    const injectScriptThenMount = (view: TmsSaveCardView) => {
        // Avoid double-injecting the merchant-services script if it's already on the page.
        if (window.TessituraMerchantServices && typeof window.TessituraMerchantServices.PaymentComponent === 'function') {
            mountComponent(view);
            return;
        }
        const existing = document.querySelector<HTMLScriptElement>('script[data-tms-merchant-script]');
        if (existing) {
            existing.addEventListener('load', () => mountComponent(view));
            return;
        }
        const s = document.createElement('script');
        s.src = view.url;
        if (view.checksum) {
            s.integrity = view.checksum;
            s.crossOrigin = 'anonymous';
        }
        s.setAttribute('data-tms-merchant-script', 'true');
        s.addEventListener('load', () => mountComponent(view));
        s.addEventListener('error', () => showError('Could not load Tessitura Merchant Services script.'));
        document.head.appendChild(s);
    };

    const mountComponent = (view: TmsSaveCardView) => {
        try {
            const props: Record<string, unknown> = JSON.parse(JSON.stringify(view.properties ?? {}));
            // Point the component at the caller-supplied endpoints rather than the
            // checkout-flow defaults the framework bakes in.
            props.endpoints = {
                authorize: options.authorizeUrl,
                finalize: options.finalizeUrl
            };
            // Belt-and-suspenders: server already passes enableStoredCards=false,
            // but the merchant component reads showStoredCards on its request props
            // to suppress the saved-card UI. Set every variant we've seen in TMS
            // configs so any one of them lands.
            props.showStoredCards = false;
            props.enableStoredCards = false;
            props.showStoredPaymentMethods = false;
            // Pay-button label is overridden via CSS in _tms-save-card.scss
            // (hide .adyen-checkout__button__text + ::after on __content) — no need
            // to push translations props.

            const merch = new window.TessituraMerchantServices!.PaymentComponent(props);
            merch.mount('#tms-save-card-container');
            component = merch;
            mounted = true;
            loading = false;
            hideLoader();
            // Component is fully rendered — NOW open the modal. The user sees the
            // full-screen loader instead of an empty modal flashing during fetch +
            // script injection + mount.
            showModal();
        } catch (err) {
            console.error('[tms-save-card] mount failed:', err);
            showError();
        }
    };

    openers.forEach(btn => {
        btn.addEventListener('click', async (e: Event) => {
            try {
                e.preventDefault();
                if (loading) return;
                if (mounted && !options.remountOnOpen) {
                    // Already mounted from a prior click — just reopen the modal.
                    showModal();
                    return;
                }
                if (options.beforeOpen) {
                    const ok = await Promise.resolve(options.beforeOpen());
                    if (!ok) return;
                }
                loadAndMount();
            } catch (err) {
                console.error('[tms-save-card] click handler threw:', err);
                hideLoader();
                showError('Unexpected error opening the save-card form.');
            }
        });
    });

    modal.querySelectorAll<HTMLElement>('[data-a11y-dialog-hide]').forEach(closer => {
        closer.addEventListener('click', () => hideModal());
    });

    document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') hideModal();
    });
}

/**
 * Membership portal entrypoint — wires the default save-card endpoints. Free
 * trial signup uses `mountTmsSaveCardModal` directly with its own endpoint
 * URLs and a `beforeOpen` hook for address validation + save.
 */
export function initTmsSaveCardModal(): void {
    mountTmsSaveCardModal({
        authorizeUrl: SAVE_CARD_AUTHORIZE_ENDPOINT,
        finalizeUrl: SAVE_CARD_FINALIZE_ENDPOINT,
        // A declined card leaves the merchant component in a terminal error state, so
        // reopening without a fresh mount hands the patron back the dead form with no way
        // to retry short of reloading. Safe here because this flow passes no beforeOpen.
        remountOnOpen: true,
    });
}
